import { useCurrentAccount, useSuiClient, useSuiClientQuery } from '@mysten/dapp-kit';
import { useCallback, useMemo, useState } from 'react';
import toast from 'react-hot-toast';

import {
	buildStakeCpcForOwner,
	dgcToMist,
	getCpcBalance,
	maxStakeMistFromBalance,
	mistToDgc,
	type StakeBuildClient,
} from '@cpc/tx';

import type { ValidatorOption } from '~/components/SelectValidator';
import { useCpcSignAndExecuteTransaction } from '~/hooks/useCpcSignAndExecuteTransaction';
import { DGC_TYPE_ARG } from '~/utils/cpcCoin';
import { formatMistAsDgc, mistToBigInt } from '~/utils/explorerFormat';

function formatStakeError(err: unknown, stakeMist: bigint): string {
	const stakeStr = mistToDgc(stakeMist);
	const msg = err instanceof Error ? err.message : String(err);
	const m = /Available amount in account.*?is less than requested:\s*(\d+)\s*<\s*(\d+)/.exec(
		msg,
	);
	if (m) {
		const available = BigInt(m[1]!);
		const requested = BigInt(m[2]!);
		const diff = requested - available;
		return (
			`质押数量仍是 ${stakeStr} RWAX（未改成全额）。` +
			`链上模拟时地址余额还要另预留约 ${mistToDgc(diff)} RWAX 作 Gas，` +
			`合计检查 ${mistToDgc(requested)} RWAX > 可用 ${mistToDgc(available)} RWAX。` +
			`请略减质押数量，或先合并零散 Coin 后重试。`
		);
	}
	if (/withdraw reservation|Insufficient/i.test(msg)) {
		return `质押 ${stakeStr} RWAX 失败：地址余额不足（Gas 需另留，不会从质押数量里扣）`;
	}
	return msg || '质押失败，请检查余额与 gas';
}

export function useCpcStakeFlow() {
	const account = useCurrentAccount();
	const client = useSuiClient();
	const { mutateAsync: signAndExecute, isPending: isStaking } =
		useCpcSignAndExecuteTransaction();

	const [isStakeOpen, setIsStakeOpen] = useState(false);
	const [isValidatorOpen, setIsValidatorOpen] = useState(false);
	const [stakeAmount, setStakeAmount] = useState('');
	const [validatorOptions, setValidatorOptions] = useState<ValidatorOption[]>([]);

	const { data: balanceData } = useSuiClientQuery(
		'getBalance',
		{ owner: account?.address ?? '', coinType: DGC_TYPE_ARG },
		{ enabled: !!account?.address },
	);

	const availableMist = balanceData?.totalBalance ?? balanceData?.fundsInAddressBalance ?? '0';
	const addressBalanceMist = balanceData?.fundsInAddressBalance ?? '0';
	const looseCoinMist = useMemo(() => {
		const total = mistToBigInt(availableMist);
		const ab = mistToBigInt(addressBalanceMist);
		return total > ab ? total - ab : 0n;
	}, [availableMist, addressBalanceMist]);

	const coinObjectCount = Number(balanceData?.coinObjectCount ?? 0);

	const stakeableMist = useMemo(
		() =>
			maxStakeMistFromBalance({
				totalMist: mistToBigInt(availableMist),
				addressBalanceMist: mistToBigInt(addressBalanceMist),
				looseCoinMist,
				coinObjectCount,
			}),
		[availableMist, addressBalanceMist, looseCoinMist, coinObjectCount],
	);

	const availableCpc = formatMistAsDgc(availableMist);
	const stakeableCpc = formatMistAsDgc(stakeableMist);

	const openStakeModal = useCallback(() => {
		if (!account) {
			toast.error('请先连接钱包');
			return;
		}
		if (stakeableMist <= 0n) {
			toast.error('RWAX 可质押余额不足（需预留 Gas）');
			return;
		}
		setValidatorOptions([]);
		setIsStakeOpen(true);
	}, [account, stakeableMist]);

	const presetValidators = useCallback((options: ValidatorOption[]) => {
		setValidatorOptions(options);
		setIsStakeOpen(true);
	}, []);

	const handleStakeModalConfirm = useCallback(
		(amount: string, validators: ValidatorOption[]) => {
			setStakeAmount(amount);
			if (validators.length) {
				setValidatorOptions(validators);
			}
			setIsStakeOpen(false);
			setIsValidatorOpen(true);
		},
		[],
	);

	const executeStake = useCallback(
		async (validatorAddress: string) => {
			if (!account?.address) {
				toast.error('请先连接钱包');
				return;
			}
			let amountMist: bigint;
			try {
				amountMist = dgcToMist(stakeAmount);
			} catch {
				toast.error('请输入有效质押数量');
				return;
			}
			if (amountMist <= 0n) {
				toast.error('请输入有效质押数量');
				return;
			}

			const bal = await getCpcBalance(
				client as unknown as StakeBuildClient,
				account.address,
			);
			const maxStake = maxStakeMistFromBalance(bal);
			if (amountMist > maxStake) {
				toast.error('超过可质押余额（请预留 Gas，或先合并零散 Coin）');
				return;
			}

			try {
				const tx = await buildStakeCpcForOwner(
					client as unknown as StakeBuildClient,
					account.address,
					{ validatorAddress, amountMist },
				);
				await signAndExecute({ transaction: tx });
				toast.success('质押成功');
				setStakeAmount('');
				setIsValidatorOpen(false);
			} catch (err) {
				console.error(err);
				toast.error(formatStakeError(err, amountMist));
			}
		},
		[account?.address, client, signAndExecute, stakeAmount],
	);

	return {
		account,
		isStakeOpen,
		setIsStakeOpen,
		isValidatorOpen,
		setIsValidatorOpen,
		stakeAmount,
		validatorOptions,
		availableMist,
		availableCpc,
		stakeableCpc,
		isStaking,
		openStakeModal,
		presetValidators,
		handleStakeModalConfirm,
		executeStake,
	};
}
